import React, { FC } from 'react';
import { Box, Portal, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { DotLoader } from '@ui/DotLoader';
import { CsvDropState } from './use-csv-file-drop.hook';

type DropZoneOverlayProps = {
  labels?: Partial<{
    importingSubtitle: string;
    invalidSubtitle: string;
    invalidTitle: string;
    neutralSubtitle: string;
    neutralTitle: string;
  }>;
  state: CsvDropState;
};

type OverlayTone = 'neutral' | 'invalid' | 'importing';

type IconComponent = typeof CloudUploadOutlinedIcon;

type OverlayVisuals = {
  Icon: IconComponent;
  subtitle: React.ReactNode;
  title: React.ReactNode;
  tone: OverlayTone;
};

const buildVisuals = (
  state: CsvDropState,
  labels: DropZoneOverlayProps['labels'],
): OverlayVisuals | null => {
  if (state.kind === 'idle') return null;
  if (state.kind === 'importing') {
    return {
      Icon: CloudUploadOutlinedIcon,
      subtitle: labels?.importingSubtitle ?? state.filename,
      title: (
        <>
          Importing
          <DotLoader />
        </>
      ),
      tone: 'importing',
    };
  }
  if (state.validity === 'invalid') {
    return {
      Icon: BlockOutlinedIcon,
      subtitle:
        labels?.invalidSubtitle ??
        'Only single .csv or .xlsx files are supported',
      title: labels?.invalidTitle ?? 'Unsupported file',
      tone: 'invalid',
    };
  }
  return {
    Icon: CloudUploadOutlinedIcon,
    subtitle: labels?.neutralSubtitle ?? 'Release to import',
    title: labels?.neutralTitle ?? 'Drop CSV or xlsx file',
    tone: 'neutral',
  };
};

const PANEL_SIZE = 294;
const CONTAINER_WIDTH = 101;
const CONTAINER_HEIGHT = 71;
const ICON_SIZE = 48;
const FONT_FAMILY = '"Roboto", "Roboto-Regular", Helvetica';

const useStyles = makeStyles<{ tone: OverlayTone }>()((theme, { tone }) => {
  const isInvalid = tone === 'invalid';

  return {
    backdrop: {
      alignItems: 'center',
      backdropFilter: 'blur(2px)',
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      display: 'flex',
      inset: 0,
      justifyContent: 'center',
      pointerEvents: 'none',
      position: 'fixed',
      zIndex: theme.zIndex.modal + 1,
    },
    panel: {
      backgroundColor: 'rgba(255, 255, 255, 0.72)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      outline: '1px solid',
      outlineColor: isInvalid
        ? theme.palette.error.main
        : 'rgba(255, 255, 255, 0.55)',
      borderRadius: 20,
      boxShadow:
        '0 1px 2px rgba(0, 0, 0, 0.06), inset 0 0 10px rgba(0, 0, 0, 0.06)',
      minHeight: PANEL_SIZE,
      minWidth: PANEL_SIZE,
      position: 'relative',
      width: PANEL_SIZE,
    },
    container: {
      height: CONTAINER_HEIGHT,
      left: `calc(50% - ${CONTAINER_WIDTH / 2 + 0.5}px)`,
      position: 'absolute',
      top: `calc(50% - ${CONTAINER_HEIGHT / 2 + 11.5}px)`,
      width: CONTAINER_WIDTH,
    },
    uploadIcon: {
      aspectRatio: 1,
      color: isInvalid ? theme.palette.error.main : 'rgba(0, 0, 0, 0.55)',
      fontSize: ICON_SIZE,
      height: ICON_SIZE,
      left: `calc(50% - ${ICON_SIZE / 2}px)`,
      position: 'absolute',
      top: `calc(50% - ${ICON_SIZE / 2 + 12}px)`,
      width: ICON_SIZE,
    },
    title: {
      alignItems: 'center',
      color: isInvalid ? theme.palette.error.main : '#000000',
      display: 'flex',
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      fontWeight: 400,
      gap: theme.spacing(0.5),
      justifyContent: 'center',
      left: '50%',
      letterSpacing: '0.64px',
      lineHeight: '24px',
      opacity: isInvalid ? 1 : 0.8,
      position: 'absolute',
      textAlign: 'center',
      top: 'calc(50% + 24px)',
      transform: 'translateX(-50%)',
      whiteSpace: 'nowrap',
    },
    subtitle: {
      color: '#000000',
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: 400,
      left: '50%',
      letterSpacing: '0.52px',
      lineHeight: '19.5px',
      maxWidth: `calc(100% - ${theme.spacing(4)})`,
      opacity: 0.4,
      overflow: 'hidden',
      position: 'absolute',
      textAlign: 'center',
      textOverflow: 'ellipsis',
      top: 'calc(50% + 82px)',
      transform: 'translateX(-50%)',
      whiteSpace: 'nowrap',
    },
  };
});

export const DropZoneOverlay: FC<DropZoneOverlayProps> = ({
  state,
  labels,
}) => {
  const visuals = buildVisuals(state, labels);
  const { classes } = useStyles({ tone: visuals?.tone ?? 'neutral' });

  if (!visuals) return null;

  const { Icon } = visuals;

  return (
    <Portal>
      <Box className={classes.backdrop} aria-hidden>
        <Box className={classes.panel}>
          <Box className={classes.container}>
            <Icon className={classes.uploadIcon} />
            <Typography component="div" className={classes.title}>
              {visuals.title}
            </Typography>
          </Box>
          <Typography component="div" className={classes.subtitle}>
            {visuals.subtitle}
          </Typography>
        </Box>
      </Box>
    </Portal>
  );
};
